import { useEffect, useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Cell,
} from "recharts";
import { TrendingUp, TrendingDown } from "lucide-react";
import { api } from "@/lib/api";

const GOLD = "#D4AF37";
const GOLD_SOFT = "#E6C75A";

const RANGES = [
  { label: "7D", value: 7 },
  { label: "30D", value: 30 },
  { label: "90D", value: 90 },
];

function fmtShort(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function TooltipCard({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-black border border-[#D4AF37]/40 px-3 py-2">
      <p className="font-body text-[10px] tracking-[0.18em] uppercase text-white/50">{fmtShort(label)}</p>
      <p className="font-display text-lg text-[#D4AF37]">{payload[0].value}</p>
      <p className="font-body text-[10px] text-white/50">new contacts</p>
    </div>
  );
}

export default function AnalyticsPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get("/admin/analytics", { params: { days } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.detail || "Failed to load analytics"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const delta = data?.period?.delta_pct;
  const hasDelta = typeof delta === "number";
  const isUp = hasDelta && delta >= 0;

  return (
    <section className="mt-4" data-testid="analytics-panel">
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="font-body text-xs tracking-[0.2em] uppercase text-white/40">Growth</p>
          <h2 className="font-display text-3xl text-white mt-1">Contact acquisition</h2>
        </div>
        <div className="flex items-center gap-1 border border-white/12 p-1">
          {RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setDays(r.value)}
              data-testid={`analytics-range-${r.value}`}
              className={`px-3 py-1.5 font-body text-xs tracking-wider uppercase transition-colors ${
                days === r.value ? "bg-[#D4AF37] text-black" : "text-white/60 hover:text-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="border border-white/12 p-6" data-testid="kpi-period-new">
          <p className="font-body text-xs tracking-[0.16em] uppercase text-white/45">New in period</p>
          <div className="flex items-baseline gap-3 mt-3">
            <p className="font-display text-4xl text-white">{data?.period?.new_contacts ?? "—"}</p>
            {hasDelta ? (
              <span className={`inline-flex items-center gap-1 font-body text-xs ${isUp ? "text-emerald-400" : "text-rose-400"}`}>
                {isUp ? <TrendingUp size={13} /> : <TrendingDown size={13} />} {Math.abs(delta)}%
              </span>
            ) : data ? (
              <span className="font-body text-xs text-[#D4AF37] tracking-wider uppercase">New</span>
            ) : null}
          </div>
          <p className="font-body text-[11px] text-white/40 mt-1">vs previous {days} days</p>
        </div>
        <div className="border border-white/12 p-6" data-testid="kpi-total">
          <p className="font-body text-xs tracking-[0.16em] uppercase text-white/45">Total contacts</p>
          <p className="font-display text-4xl text-white mt-3">{data?.total_contacts ?? "—"}</p>
          <p className="font-body text-[11px] text-white/40 mt-1">all-time in CRM</p>
        </div>
        <div className="border border-white/12 p-6" data-testid="kpi-conversion">
          <p className="font-body text-xs tracking-[0.16em] uppercase text-white/45">Book interest rate</p>
          <p className="font-display text-4xl text-white mt-3">
            {data?.funnel?.find((f) => f.key === "book_interest")?.rate ?? "—"}
            <span className="text-lg text-white/40">%</span>
          </p>
          <p className="font-body text-[11px] text-white/40 mt-1">of total contacts</p>
        </div>
      </div>

      {/* Growth chart */}
      <div className="border border-white/12 p-6 mb-8" data-testid="growth-chart">
        <p className="font-body text-xs tracking-[0.16em] uppercase text-white/45 mb-4">New contacts per day</p>
        <div className="h-64">
          {loading ? (
            <div className="flex items-center justify-center h-full text-white/40 font-body">Loading…</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.growth || []} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={GOLD} stopOpacity={0.55} />
                    <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={fmtShort}
                  tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }}
                  axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
                  tickLine={false}
                  minTickGap={24}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={30}
                />
                <Tooltip content={<TooltipCard />} cursor={{ stroke: GOLD, strokeOpacity: 0.3 }} />
                <Area type="monotone" dataKey="count" stroke={GOLD} strokeWidth={2} fill="url(#goldFill)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Funnel + Top sources */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-white/12 p-6" data-testid="funnel-widget">
          <p className="font-body text-xs tracking-[0.16em] uppercase text-white/45 mb-6">Conversion funnel</p>
          <div className="space-y-4">
            {(data?.funnel || []).map((step, i) => {
              const top = data?.funnel?.[0]?.count || 0;
              const width = top > 0 ? Math.max(6, (step.count / top) * 100) : 0;
              return (
                <div key={step.key} data-testid={`funnel-${step.key}`}>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <span className="font-body text-sm text-white/80">{step.label}</span>
                    <span className="font-body text-xs text-white/50">
                      <span className="text-white">{step.count}</span> · {step.rate}%
                    </span>
                  </div>
                  <div className="h-2 bg-white/6">
                    <div
                      className="h-full transition-all"
                      style={{
                        width: `${width}%`,
                        background: i === 0 ? GOLD : `linear-gradient(90deg, ${GOLD}, ${GOLD_SOFT})`,
                        opacity: 1 - i * 0.14,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border border-white/12 p-6" data-testid="top-sources-widget">
          <p className="font-body text-xs tracking-[0.16em] uppercase text-white/45 mb-4">Top landing pages</p>
          <div className="h-56">
            {(data?.top_sources || []).length === 0 ? (
              <div className="flex items-center justify-center h-full text-white/40 font-body text-sm">
                No data yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.top_sources} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
                  <XAxis type="number" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="source" tick={{ fill: "rgba(255,255,255,0.7)", fontSize: 11 }} axisLine={false} tickLine={false} width={110} />
                  <Tooltip cursor={{ fill: "rgba(212,175,55,0.08)" }} contentStyle={{ background: "#000", border: "1px solid rgba(212,175,55,0.4)", fontSize: 12 }} labelStyle={{ color: "#D4AF37" }} />
                  <Bar dataKey="count" fill={GOLD} radius={[0, 0, 0, 0]}>
                    {data.top_sources.map((_, i) => (
                      <Cell key={i} fill={i === 0 ? GOLD : GOLD_SOFT} fillOpacity={1 - i * 0.09} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
